// Client-side thumbnail generation via canvas. Kept in its own module and
// fully injectable (`createImage`, `createCanvas`) so tests can run in jsdom
// without touching any real browser image decoder.
//
// Contract:
//   - Input: File (image), max longest-edge 320px, JPEG q0.8
//   - Output: Blob | null. A null result is a soft failure — the caller
//     uploads the original anyway and the grid falls back to it.

const DEFAULT_LONGEST_EDGE = 320;
const DEFAULT_QUALITY = 0.8;

function defaultCreateImage() {
  return new Image();
}

function defaultCreateCanvas(width, height) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

function loadImage(file, createImage) {
  return new Promise((resolve, reject) => {
    const img = createImage();
    const url = URL.createObjectURL(file);
    const cleanup = () => URL.revokeObjectURL(url);
    img.onload = () => { cleanup(); resolve(img); };
    img.onerror = (err) => { cleanup(); reject(err); };
    img.src = url;
  });
}

function fitTo(longestEdge, width, height) {
  if (!width || !height) return { width: longestEdge, height: longestEdge };
  const scale = Math.min(1, longestEdge / Math.max(width, height));
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

function canvasToBlob(canvas, mime, quality) {
  return new Promise((resolve) => {
    if (typeof canvas.toBlob === 'function') {
      canvas.toBlob(resolve, mime, quality);
    } else {
      resolve(null);
    }
  });
}

// Generate a JPEG thumbnail for `file`. Returns null on any failure — the
// original photo is authoritative so a bad thumbnail must never block the
// upload.
export async function generateThumbnail(file, {
  longestEdge = DEFAULT_LONGEST_EDGE,
  quality = DEFAULT_QUALITY,
  createImage = defaultCreateImage,
  createCanvas = defaultCreateCanvas,
} = {}) {
  try {
    const img = await loadImage(file, createImage);
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    const { width, height } = fitTo(longestEdge, w, h);
    const canvas = createCanvas(width, height);
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, width, height);
    const blob = await canvasToBlob(canvas, 'image/jpeg', quality);
    return blob || null;
  } catch (_err) {
    return null;
  }
}
